import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { hueVars, acquireMailto } from "../data/artists";
import ArtworkMedia from "./ArtworkMedia";

gsap.registerPlugin(ScrollTrigger);

const positions = (n, light, landscape) => {
  if (light) {
    /* level row hung high — frames sit directly under the header text.
       Portrait frames are tall, so they hang narrower and a touch lower */
    if (n === 1) return [{ x: 50, y: 32, w: landscape ? 36 : 24 }];
    if (n === 2) {
      const w = landscape ? 30 : 20;
      return [{ x: 31, y: 30, w }, { x: 69, y: 30, w }];
    }
    if (n === 3) {
      return landscape
        ? [{ x: 18.5, y: 28, w: 29 }, { x: 50, y: 28, w: 29 }, { x: 81.5, y: 28, w: 29 }]
        : [{ x: 20, y: 30, w: 17 }, { x: 50, y: 30, w: 17 }, { x: 80, y: 30, w: 17 }];
    }
    const w = Math.min(landscape ? 30 : 20, 88 / n);
    return Array.from({ length: n }, (_, i) => ({
      x: (100 / (n + 1)) * (i + 1),
      y: 30,
      w,
    }));
  }
  if (n === 1) return [{ x: 50, y: 50, w: 30 }];
  if (n === 2) return [{ x: 32, y: 50, w: 24 }, { x: 68, y: 50, w: 24 }];
  if (n === 3) return [{ x: 22, y: 46, w: 20 }, { x: 50, y: 54, w: 20 }, { x: 78, y: 46, w: 20 }];
  return Array.from({ length: n }, (_, i) => ({
    x: (100 / (n + 1)) * (i + 1),
    y: i % 2 ? 55 : 46,
    w: Math.min(22, 78 / n),
  }));
};

export default function GalleryWallZoom({ artist }) {
  const works = artist.pieces || [artist.piece];
  const landscape = !!artist.pieces || !!artist.landscape;
  const square = !!artist.square;
  const pos = positions(works.length, artist.wallStyle !== "dark", landscape)
    .map((p, i) => (works[i] && works[i].tall ? { ...p, w: p.w * 0.62 } : p));
  const frameCls = (w) => `gw-frame${w.tall ? " tall" : landscape ? " wide" : square ? " square" : ""}`;
  const sectionRef = useRef(null);
  const stageRef = useRef(null);
  const triggerRef = useRef(null);
  const timelineRef = useRef(null);
  const timesRef = useRef([]);
  const videoRefs = useRef([]);
  const stillRefs = useRef([]);
  const [played, setPlayed] = useState(false);
  const [focus, setFocus] = useState(null);
  const [focusView, setFocusView] = useState(0);
  const [seqView, setSeqView] = useState(0);
  const seqViewRef = useRef(0);
  const activeView = focus && focus.views ? focus.views[focusView] : focus;
  useEffect(() => {
    setPlayed(false);
    setFocus(null);
    setSeqView(0);
    seqViewRef.current = 0;
  }, [artist.name]);
  const reduced =
    typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  /* small screens skip the pinned sequence and get a full-width static wall instead */
  const isMobile =
    typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;

  useLayoutEffect(() => {
    if (reduced || played || isMobile) return;
    const lenis = window.__lenis;
    if (lenis) lenis.on("scroll", ScrollTrigger.update);

    const ctx = gsap.context(() => {
      const stage = stageRef.current;
      const infos = gsap.utils.toArray(".gw-info", sectionRef.current);
      const pieceEls = gsap.utils.toArray(".gw-piece", sectionRef.current);
      gsap.set(infos, { autoAlpha: 0, y: 26 });

      const calc = (i) => {
        const vw = stage.offsetWidth;
        const vh = stage.offsetHeight;
        const p = pos[i];
        const desktop = window.innerWidth >= 768;
        const cx = desktop ? 0.3 * vw : 0.5 * vw;
        const cy = desktop ? 0.5 * vh : 0.34 * vh;
        const targetW = (desktop ? 0.56 : 0.86) * vw;
        const s = targetW / ((p.w / 100) * vw);
        return { x: cx - (p.x / 100) * vw * s, y: cy - (p.y / 100) * vh * s, scale: s };
      };

      const zoomTo = (i) => ({
        x: () => calc(i).x,
        y: () => calc(i).y,
        scale: () => calc(i).scale,
        duration: 1.2,
      });

      const tl = gsap.timeline({ defaults: { ease: "power2.inOut" } });
      timelineRef.current = tl;

      const times = [];
      const starts = [];
      let t = 0;
      works.forEach((w, i) => {
        starts.push(t);
        tl.to(stage, zoomTo(i), t);
        const others = pieceEls.filter((_, j) => j !== i);
        if (others.length) {
          tl.to(others, { autoAlpha: 0, duration: 0.5, ease: "power2.out" }, t + 0.15);
          tl.to(pieceEls[i], { autoAlpha: 1, duration: 0.3 }, t + 0.15);
        }
        tl.to(infos[i], { autoAlpha: 1, y: 0, duration: 0.4, ease: "power2.out" }, t + 1.0);
        /* pieces with views hold the zoom longer — one segment per view */
        const h = w.views ? 1.2 + w.views.length * 1.6 : 2.4;
        if (i < works.length - 1) {
          tl.to(infos[i], { autoAlpha: 0, y: -18, duration: 0.3, ease: "power2.in" }, t + h - 0.3);
        }
        times.push(t + 1.4);
        t += h;
      });
      tl.to(infos[works.length - 1], { autoAlpha: 0, y: -18, duration: 0.3, ease: "power2.in" }, t);
      tl.to(stage, { x: 0, y: 0, scale: 1, duration: 0.9 }, t + 0.15);
      if (works.length > 1) {
        tl.to(pieceEls, { autoAlpha: 1, duration: 0.6, ease: "power2.out" }, t + 0.15);
      }
      timesRef.current = times;
      const zoomOutStart = t + 0.15;

      /* playOnZoom pieces: still on the wall, play while zoomed in, back to still on leave */
      const syncVideos = () => {
        const time = tl.time();
        works.forEach((w, i) => {
          if (!w.playOnZoom) return;
          const v = videoRefs.current[i];
          if (!v) return;
          const end = i < works.length - 1 ? starts[i + 1] : zoomOutStart;
          const active = time >= starts[i] + 0.1 && time < end;
          /* cycle through a piece's views while its zoom holds */
          if (w.views) {
            const idx = active
              ? Math.max(0, Math.min(w.views.length - 1, Math.floor((time - starts[i] - 1.1) / 1.6)))
              : 0;
            if (idx !== seqViewRef.current) {
              seqViewRef.current = idx;
              setSeqView(idx);
            }
          }
          if (active && v.paused) {
            v.play().catch(() => {});
          } else if (!active && !v.paused) {
            v.pause();
            try { v.currentTime = 0; } catch (e) { /* not seekable yet */ }
          }
          const still = stillRefs.current[i];
          if (still) still.style.opacity = active ? "0" : "1";
        });
      };
      tl.eventCallback("onUpdate", syncVideos);

      triggerRef.current = ScrollTrigger.create({
        animation: tl,
        trigger: sectionRef.current,
        start: "top top",
        end: () => "+=" + Math.round((tl.duration() * window.innerHeight) / 2.3),
        pin: true,
        scrub: 1,
        invalidateOnRefresh: true,
        anticipatePin: 1,
        onLeave: () => setPlayed(true),
      });
    }, sectionRef);

    return () => {
      ctx.revert();
      triggerRef.current = null;
      timelineRef.current = null;
      if (lenis) lenis.off("scroll", ScrollTrigger.update);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artist.name, reduced, played, isMobile]);

  /* after the first full viewing the pin is gone — park the visitor at the static wall */
  useLayoutEffect(() => {
    if (!played) return;
    const el = sectionRef.current;
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY;
    if (window.__lenis) window.__lenis.scrollTo(y, { immediate: true, force: true });
    else window.scrollTo(0, y);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [played]);

  /* focus overlay: freeze page scroll, close on Escape */
  useEffect(() => {
    const lenis = window.__lenis;
    if (focus) {
      if (lenis) lenis.stop();
      document.body.style.overflow = "hidden";
      const onKey = (e) => {
        if (e.key === "Escape") setFocus(null);
      };
      document.addEventListener("keydown", onKey);
      return () => {
        document.removeEventListener("keydown", onKey);
        if (lenis) lenis.start();
        document.body.style.overflow = "";
      };
    }
  }, [focus]);

  const jumpTo = (i) => {
    const st = triggerRef.current;
    const tl = timelineRef.current;
    if (!st || !tl) return;
    const progress = Math.min((timesRef.current[i] + 0.3) / tl.duration(), 0.99);
    const y = st.start + progress * (st.end - st.start);
    if (window.__lenis) window.__lenis.scrollTo(y, { duration: 1.4 });
    else window.scrollTo({ top: y, behavior: "smooth" });
  };

  const wallPiece = (w, i, onActivate, paused, seq) => {
    /* during the first sequence a piece with views cycles its films inside the zoom */
    const m = seq && w.views
      ? { ...w, image: (w.views[seqView] || {}).image || w.image, video: (w.views[seqView] || {}).video, videoWebm: (w.views[seqView] || {}).videoWebm }
      : w;
    return (
    <button
      key={`${w.title}-${i}`}
      className="gw-piece"
      data-testid={`gw-piece-${i + 1}`}
      style={seq ? { left: `${pos[i].x}%`, top: `${pos[i].y}%`, width: `${pos[i].w}%` } : undefined}
      onClick={onActivate}
      aria-label={`View ${w.title} by ${artist.name}`}
    >
      <span className={frameCls(w)} style={hueVars(artist.hue)}>
        {m.image || m.video ? (
          <>
            <ArtworkMedia
              key={seq ? `seq-${seqView}` : "rest"}
              piece={m}
              alt={`${w.title} by ${artist.name}`}
              paused={paused && !!w.playOnZoom}
              mediaRef={(el) => { videoRefs.current[i] = el; }}
            />
            {paused && w.playOnZoom && w.video && w.image && (
              <img
                className="artwork gw-still"
                src={w.image}
                alt=""
                aria-hidden="true"
                ref={(el) => { stillRefs.current[i] = el; }}
              />
            )}
          </>
        ) : (
          <>
            <span className="texture" />
            <span className="gw-await">Awaiting artwork</span>
          </>
        )}
      </span>
      <span className="gw-plate">{w.title}</span>
    </button>
    );
  };

  if (reduced || played || isMobile) {
    const stillMode = played && !reduced && !isMobile;
    const open = (w) => () => { setFocus(w); setFocusView(0); };
    return (
      <section className="gw gw-static" ref={sectionRef} data-testid="gallery-wall-zoom">
        <div className="gw-static-wall">{works.map((w, i) => wallPiece(w, i, open(w), stillMode, false))}</div>
        <AnimatePresence>
          {focus && (
            <motion.div
              className="gw-focus"
              data-testid="gw-focus"
              data-lenis-prevent
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => {
                if (e.target === e.currentTarget) setFocus(null);
              }}
            >
              <button
                className="gw-focus-close"
                data-testid="gw-focus-close-button"
                aria-label="Close"
                onClick={() => setFocus(null)}
              >
                ×
              </button>
              <motion.div
                className="gw-focus-inner"
                initial={{ opacity: 0, y: 26 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 14 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="gw-focus-frame">
                  <span className={frameCls({ ...focus, tall: activeView.tall || focus.tall })} style={hueVars(artist.hue)}>
                    {activeView.image || activeView.video ? (
                      <ArtworkMedia key={focusView} piece={activeView} alt={`${focus.title} by ${artist.name} — ${activeView.label || "the work"}`} />
                    ) : (
                      <>
                        <span className="texture" />
                        <span className="gw-await">Awaiting artwork</span>
                      </>
                    )}
                  </span>
                  <span className="gw-plate">{focus.title}</span>
                  {focus.views && (
                    <span className="gw-focus-views">
                      {focus.views.map((v, vi) => (
                        <button
                          key={v.label}
                          className={`gw-focus-view${vi === focusView ? " on" : ""}`}
                          data-testid={`gw-focus-view-${vi + 1}`}
                          onClick={() => setFocusView(vi)}
                        >
                          {v.label}
                        </button>
                      ))}
                    </span>
                  )}
                </div>
                <aside className="gw-focus-info" data-testid="gw-focus-info">
                  <h4 className="display">{focus.title}</h4>
                  <div className="gw-artist">{artist.name}</div>
                  <div className="gw-rows">
                    <div className="gw-row"><span>Medium</span><span>{focus.medium}</span></div>
                    <div className="gw-row"><span>Size</span><span>{focus.size}</span></div>
                    <div className="gw-row"><span>Edition</span><span>{focus.edition}</span></div>
                    <div className="gw-row"><span>Year</span><span>{focus.year}</span></div>
                  </div>
                  <div className="gw-price" data-testid="gw-focus-price">{focus.price}</div>
                  <a
                    className="gw-enquire"
                    data-testid="gw-focus-enquire"
                    href={acquireMailto({ name: artist.name, piece: focus })}
                  >
                    Enquire
                  </a>
                </aside>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </section>
    );
  }

  return (
    <section className="gw" ref={sectionRef} data-testid="gallery-wall-zoom">
      <div className="gw-pin">
        <div className="gw-stage" ref={stageRef}>
          <div className="gw-wall" aria-hidden="true" />
          {works.map((w, i) => wallPiece(w, i, () => jumpTo(i), true, true))}
        </div>
        {works.map((w, i) => (
          <aside className="gw-info" key={i} data-testid={`gw-info-${i + 1}`}>
            <div className="gw-info-inner">
              <h4 className="display">{w.title}</h4>
              <div className="gw-artist">{artist.name}</div>
              {w.views && <div className="gw-view-label" data-testid={`gw-view-label-${i + 1}`}>{(w.views[seqView] || {}).label}</div>}
              <div className="gw-rows">
                <div className="gw-row"><span>Medium</span><span>{w.medium}</span></div>
                <div className="gw-row"><span>Size</span><span>{w.size}</span></div>
                <div className="gw-row"><span>Edition</span><span>{w.edition}</span></div>
                <div className="gw-row"><span>Year</span><span>{w.year}</span></div>
              </div>
              <div className="gw-price">{w.price}</div>
              <a className="gw-enquire" data-testid={`gw-enquire-${i + 1}`} href={acquireMailto({ name: artist.name, piece: w })}>
                Enquire
              </a>
            </div>
          </aside>
        ))}
      </div>
    </section>
  );
}
